import { formatAed, formatToInternational } from '@shared/utils'
import { formatBand } from '../../trading'

// Sell-side input handling and instant checks for the create-listing drawer
// (Figma 34433:273157 → 273212 / 273271 → 273313). Pure functions — the
// drawer's composable and components share them. Fees and proceeds are
// priced by the server (POST sell-quote); these checks only give inline
// feedback before the quote lands, using the server's own band / minimum.

// Figma prints every sell figure with two decimals ("AED 9,500.00") — the
// shared trading formatter does exactly that.
export { formatAmount } from '../../trading'

/** "938" / "-" — token counts; zero reads as a dash, as in Figma. */
export const formatTokenCount = (tokens: number): string =>
  tokens > 0 ? formatToInternational(tokens) : '-'

/** Typed token field → whole tokens (0 when empty). */
export const parseTokens = (text: string): number => {
  const value = Number.parseInt(text, 10)
  return Number.isFinite(value) && value > 0 ? value : 0
}

/** Typed price field → AED per token, or null when it isn't a positive number. */
export const parsePrice = (text: string): number | null => {
  if (!text.trim()) return null
  const value = Number(text)
  return Number.isFinite(value) && value > 0 ? value : null
}

/** Keeps only digits — the token field accepts whole tokens. */
export const sanitizeTokens = (text: string): string => text.replace(/\D/g, '')

/** Digits and one decimal point, at most two decimals — the price field. */
export const sanitizePrice = (text: string): string => {
  const [whole = '', ...rest] = text.replace(/[^\d.]/g, '').split('.')
  if (!rest.length) return whole
  return `${whole}.${rest.join('').slice(0, 2)}`
}

export const isInsideBand = (price: number, bandMin: number, bandMax: number): boolean =>
  price >= bandMin && price <= bandMax

/** The server's listing rules for one holding (sell-overview). */
export interface SellListingRules {
  available: number
  bandMin: number
  bandMax: number
  /** Minimum listing value in AED. */
  minListingAmount: number
  /** Holding is worth less than the minimum — listing everything is allowed. */
  isSellAllRemainingEligible: boolean
}

/** Listing every sellable token waives the minimum value (BR sell-all-remaining). */
const isSellAll = (tokens: number, rules: SellListingRules): boolean =>
  rules.isSellAllRemainingEligible && tokens === rules.available

/** Step 1 — first problem with the quantity, or null when it can be listed. */
export function quantityError(tokens: number, rules: SellListingRules): string | null {
  if (rules.available <= 0) return 'You have no tokens available to list right now.'
  if (tokens <= 0) return 'Enter how many tokens to list.'
  if (tokens > rules.available)
    return `You have ${formatToInternational(rules.available)} tokens available to list.`
  // Even at the top of the band these tokens can't reach the minimum value.
  if (!isSellAll(tokens, rules) && tokens * rules.bandMax < rules.minListingAmount)
    return `The minimum listing value is ${formatAed(rules.minListingAmount)} — list more tokens.`
  return null
}

/** Step 2 — first problem with the price, or null when it can be listed. */
export function priceError(
  price: number | null,
  tokens: number,
  rules: SellListingRules,
): string | null {
  if (price === null) return 'Enter a price per token.'
  if (!isInsideBand(price, rules.bandMin, rules.bandMax))
    return `Set a price within ${formatBand(rules.bandMin, rules.bandMax)} per token.`
  if (!isSellAll(tokens, rules) && price * tokens < rules.minListingAmount)
    return `The minimum listing value is ${formatAed(rules.minListingAmount)} — raise the price or list more tokens.`
  return null
}
